import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Calendar, Send } from "lucide-react";

/**
 * Settings → Integrations page.
 * Hosts the Outlook Calendar Invites connection + Email "Send As" routing.
 */
export default function Integrations() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [recipientDraft, setRecipientDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/integrations/outlook-invites/status");
      setStatus(r.data);
      setRecipientDraft(r.data.recipient || "");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try {
      const r = await api.put("/integrations/outlook-invites/settings", patch);
      setStatus((s) => ({ ...s, ...r.data, connected: r.data.enabled && !!r.data.recipient }));
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/integrations/outlook-invites/test");
      toast.success(`Test invite sent to ${r.data.sent_to} — check your Outlook inbox`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading integrations…</div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="integrations-page">
      <div className="mb-8 pb-6 border-b border-zinc-200">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700 mb-2">Settings</div>
        <h1 className="text-3xl font-black tracking-tight">Integrations</h1>
        <div className="text-sm text-zinc-500 mt-1">Connect SealTech CRM to your other tools.</div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="outlook-invites-card">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <div className="text-lg font-bold">Outlook Calendar Invites</div>
              <div className="text-xs text-zinc-500">
                CRM events — scheduled jobs, assessments, maintenance visits, tasks, and appointments — are emailed to you
                as calendar invites. One click in Outlook adds them to your calendar; changes update the same event.
              </div>
            </div>
          </div>
          {status?.connected ? (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-green-50 text-green-800 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-green-200">
              <CheckCircle2 className="w-3 h-3" /> Active · {status.recipient}
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-zinc-100 text-zinc-700 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-zinc-300">
              Paused
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 text-sm font-medium" data-testid="invites-enabled-toggle">
              <input type="checkbox" checked={!!status?.enabled} onChange={() => save({ enabled: !status.enabled })} className="accent-blue-700 w-4 h-4" />
              Send calendar invites
            </label>
            <button onClick={sendTest} disabled={testing || !status?.enabled}
              className="inline-flex items-center gap-1.5 h-9 px-3 border border-blue-700 text-blue-700 hover:bg-blue-50 text-[10px] font-bold uppercase tracking-wider rounded-sm disabled:opacity-50"
              data-testid="invite-test-btn">
              <Send className="w-3.5 h-3.5" /> {testing ? "Sending…" : "Send test invite"}
            </button>
          </div>

          <div className="max-w-sm">
            <label className="block text-xs font-medium text-zinc-700 mb-1">Invites go to</label>
            <div className="flex gap-2">
              <input value={recipientDraft} onChange={(e) => setRecipientDraft(e.target.value)}
                className="flex-1 px-3 h-9 border border-zinc-300 bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-700"
                placeholder="you@outlook.com" data-testid="invite-recipient-input" />
              {recipientDraft !== (status?.recipient || "") && (
                <button onClick={() => save({ recipient: recipientDraft })}
                  className="h-9 px-3 bg-blue-700 text-white text-[10px] font-bold uppercase tracking-wider rounded-sm"
                  data-testid="invite-recipient-save">Save</button>
              )}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">Works with Outlook, Gmail, Apple Calendar — any inbox that understands calendar invites.</div>
          </div>

          <EmailRoutingPanel />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email "Send As" routing — picks which address each kind of outbound email
// is sent FROM. All addresses authenticate through the HostGator SMTP mailbox.
// ---------------------------------------------------------------------------
function EmailRoutingPanel() {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState({ saved: {}, resolved: {}, categories: [], allowed_aliases: [] });
  const [draft, setDraft] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/email-routing");
      setData(r.data);
      setDraft(r.data.saved || {});
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    reload();
  }, []);

  if (loading) {
    return (
      <div className="border-t border-zinc-200 pt-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Email &ldquo;Send As&rdquo; Routing</div>
        <div className="text-sm text-zinc-500 italic">Loading…</div>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/email-routing", draft);
      setData((d) => ({ ...d, ...r.data }));
      toast.success("Email routing saved — outbound emails will now use the matching alias.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const LABELS = {
    assessments: { icon: "📅", label: "Assessments", help: "Assessment scheduling + assessment-report emails" },
    scope:       { icon: "📝", label: "Scopes",      help: "Proposals + scope emails + sales follow-ups + stale-deal digests" },
    finance:     { icon: "💰", label: "Finance",     help: "Invoices, statements, late notices, payables reports" },
    projects:    { icon: "🛠", label: "Projects",    help: "Purchase orders, COI requests, project comms, daily status" },
    maintenance: { icon: "🟢", label: "Maintenance", help: "Maintenance visit reminders" },
  };

  const dirty = (data.categories || []).some((cat) => (draft[cat] || "") !== (data.saved?.[cat] || ""));

  return (
    <div className="border-t border-zinc-200 pt-5 mt-5" data-testid="email-routing-panel">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Email &ldquo;Send As&rdquo; Routing</div>
          <div className="text-[12px] text-zinc-600 mt-1 max-w-2xl">
            Each kind of outbound CRM email is sent from the matching address so replies land in the right inbox.
            All mail goes out through <b>mail.sealtechsolutions.co</b> (HostGator).
          </div>
        </div>
        {dirty && (
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 h-9 px-4 bg-blue-700 text-white text-[10px] font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800 disabled:opacity-50"
            data-testid="email-routing-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(data.categories || []).map((cat) => {
          const meta = LABELS[cat] || { icon: "✉️", label: cat, help: "" };
          return (
            <div key={cat} data-testid={`email-routing-row-${cat}`}>
              <label className="block text-xs font-bold text-zinc-700 mb-1">
                <span className="mr-1">{meta.icon}</span>{meta.label}
              </label>
              <select
                value={draft[cat] || ""}
                onChange={(e) => setDraft((d) => ({ ...d, [cat]: e.target.value }))}
                className="w-full px-2 h-9 border border-zinc-300 bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-700"
                data-testid={`email-routing-select-${cat}`}
              >
                <option value="">— Use primary ({data.resolved?.[cat] || "default"}) —</option>
                {(data.allowed_aliases || []).map((alias) => (
                  <option key={alias} value={alias}>{alias}</option>
                ))}
              </select>
              <div className="text-[10px] text-zinc-500 mt-1 leading-snug">{meta.help}</div>
            </div>
          );
        })}
      </div>

      {(!data.allowed_aliases || data.allowed_aliases.length === 0) && (
        <div className="mt-4 px-3 py-2 bg-amber-50 border border-amber-300 text-amber-900 text-[11px] rounded-sm">
          <b>No aliases configured.</b> Set <code>SMTP_FROM_ALIASES</code> in the backend env to a comma-separated list of allowed send-as addresses, then refresh.
        </div>
      )}
    </div>
  );
}
